import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { sendEmailDirect } from '../lib/emailTestService';

const { FiMail, FiSend, FiCheck, FiX } = FiIcons;

const EmailTestingDashboard = () => {
  // Form state
  const [formData, setFormData] = useState({
    to: '',
    subject: '',
    text: '',
    html: ''
  });

  // Validation errors
  const [errors, setErrors] = useState({});
  
  // Loading state
  const [loading, setLoading] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'success' // success or error
  });

  const validateForm = () => {
    const newErrors = {};
    if (!formData.to.trim()) newErrors.to = 'E-Mail-Adresse ist erforderlich';
    if (!formData.subject.trim()) newErrors.subject = 'Betreff ist erforderlich';

    // Basic email validation
    if (formData.to.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.to)) {
      newErrors.to = 'Ungültige E-Mail-Adresse';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear error when typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const showSnackbar = (message, type = 'success') => {
    setSnackbar({
      open: true,
      message,
      type
    });

    // Auto-hide after 5 seconds
    setTimeout(() => {
      setSnackbar(prev => ({ ...prev, open: false }));
    }, 5000);
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Füge console.log hinzu, um zu sehen, was passiert
      console.log('Starte E-Mail-Versand mit Daten:', {
        to: formData.to,
        subject: formData.subject,
        textLength: formData.text?.length || 0
      });
      
      const result = await sendEmailDirect(formData);
      console.log('E-Mail-Versand Ergebnis:', result);
      
      showSnackbar('E-Mail wurde erfolgreich gesendet');
    } catch (error) {
      console.error('Error sending email:', error);
      showSnackbar(`Fehler beim Senden: ${error.message || 'Unbekannter Fehler'}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">E-Mail Tests</h1>
          <p className="text-gray-600 mt-1">Direkter E-Mail-Versand über SMTP</p>
        </div>
      </motion.div>

      {/* Email Test Form */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
      >
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <SafeIcon icon={FiMail} className="text-primary-500 mr-2" />
          E-Mail Test-Formular
        </h2>
        
        <form className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* To field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Empfänger *
              </label>
              <input
                type="email"
                name="to"
                value={formData.to}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.to ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="beispiel@mail.de"
              />
              {errors.to && <p className="text-red-500 text-sm mt-1">{errors.to}</p>}
            </div>

            {/* Subject field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Betreff *
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.subject ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Test E-Mail Betreff"
              />
              {errors.subject && <p className="text-red-500 text-sm mt-1">{errors.subject}</p>}
            </div>
          </div>

          {/* Text content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Text-Inhalt
            </label>
            <textarea
              name="text"
              value={formData.text}
              onChange={handleInputChange}
              rows="4"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Text-Inhalt der E-Mail"
            ></textarea>
          </div>

          {/* HTML content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              HTML-Inhalt (optional)
            </label>
            <textarea
              name="html"
              value={formData.html}
              onChange={handleInputChange}
              rows="6"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
              placeholder="<div>HTML-Inhalt der E-Mail</div>"
            ></textarea>
          </div>

          {/* Submit button */}
          <div className="flex flex-wrap gap-4 pt-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSendEmail}
              disabled={loading}
              className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors flex items-center space-x-2 disabled:opacity-70"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Wird gesendet...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSend} />
                  <span>Testmail senden</span>
                </>
              )}
            </motion.button>
          </div>
        </form>
      </motion.div>

      {/* Information panel */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-blue-50 border border-blue-100 rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-blue-800 mb-2">SMTP-Konfiguration</h3>
        <p className="text-blue-700 mb-4">
          E-Mails werden direkt über den konfigurierten SMTP-Server versendet.
        </p>
        <div className="bg-white p-4 rounded-lg border border-blue-200">
          <p className="text-sm text-gray-600 mb-2">
            <span className="font-medium">SMTP-Server:</span> mail.krieg-gmbh.de:587
          </p>
          <p className="text-sm text-gray-600 mb-2">
            <span className="font-medium">Absender:</span> PV-Dashboard &lt;lack@krieg-gmbh.de&gt;
          </p>
          <p className="text-sm text-gray-600">
            <span className="font-medium">Status:</span> Direkte Verbindung aktiv
          </p>
        </div>
        
        {/* Hinweis zur Simulation */}
        <div className="mt-4 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <p className="text-sm text-yellow-800">
            <strong>Hinweis:</strong> In dieser Demo-Version werden E-Mails simuliert und nicht tatsächlich versendet. 
            In einer Produktionsumgebung würde ein echter SMTP-Server verwendet werden.
          </p>
        </div>
      </motion.div>

      {/* Snackbar */}
      {snackbar.open && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2 ${
            snackbar.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          <SafeIcon icon={snackbar.type === 'success' ? FiCheck : FiX} className="text-lg" />
          <span>{snackbar.message}</span>
          <button
            onClick={() => setSnackbar(prev => ({ ...prev, open: false }))}
            className="ml-4 text-white hover:text-gray-200"
          >
            <SafeIcon icon={FiX} />
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default EmailTestingDashboard;